import React from "react";

// True after `timeoutMs` with no user input (mouse, keys, touch, wheel);
// flips back false on input. Listeners are passive and attach once.
//
// Mouse-move wake uses a distance threshold while idle: `cursor: none`
// only repaints once the pointer moves, so the arrow lingers over the
// screensaver until the first nudge — which must NOT also dismiss it.
// A sub-threshold nudge hides the cursor; a deliberate sweep wakes.
const WAKE_MOVE_PX = 40;
// Returning to the tab mid-screensaver: hold the screensaver through
// the arrival so the focus click and incidental mouse motion of
// switching tabs don't instantly dismiss it. Deliberate input after
// the grace still wakes, and several clicks always do — insistence
// beats the grace.
const RETURN_GRACE_MS = 1200;
const GRACE_CLICKS_TO_WAKE = 2;

export default function useIdle(timeoutMs) {
  const [idle, setIdle] = React.useState(false);
  const idleRef = React.useRef(false);
  idleRef.current = idle;

  React.useEffect(() => {
    let timer = null;
    let idleMoveOrigin = null;
    let graceUntil = 0;
    let graceClicks = 0;
    const inGrace = () => performance.now() < graceUntil;
    const arm = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setIdle(true), timeoutMs);
    };
    const wake = () => {
      idleMoveOrigin = null;
      graceClicks = 0;
      setIdle(false);
      arm();
    };
    const onMouseMove = (e) => {
      if (!idleRef.current) {
        arm();
        return;
      }
      if (inGrace()) {
        idleMoveOrigin = null;
        return;
      }
      if (idleMoveOrigin == null) {
        idleMoveOrigin = { x: e.clientX, y: e.clientY };
        return;
      }
      const dist = Math.hypot(
        e.clientX - idleMoveOrigin.x,
        e.clientY - idleMoveOrigin.y,
      );
      if (dist > WAKE_MOVE_PX) wake();
    };
    const onActivity = (e) => {
      if (idleRef.current && inGrace()) {
        // Repeated clicks are unambiguous — wake even mid-grace.
        if (e.type === "mousedown" || e.type === "touchstart") {
          graceClicks += 1;
          if (graceClicks >= GRACE_CLICKS_TO_WAKE) wake();
        }
        return;
      }
      wake();
    };
    const onReturn = () => {
      if (idleRef.current) {
        graceUntil = performance.now() + RETURN_GRACE_MS;
        idleMoveOrigin = null;
        graceClicks = 0;
      }
    };
    const onVisibility = () => {
      if (!document.hidden) onReturn();
    };
    // Shut Down → Stand by: force the screensaver on demand, with the
    // same return-grace as tab-arrival so click noise can't wake it.
    const onForceIdle = () => {
      if (timer) clearTimeout(timer);
      graceUntil = performance.now() + RETURN_GRACE_MS;
      idleMoveOrigin = null;
      graceClicks = 0;
      setIdle(true);
    };
    // Capture phase: the Screensaver's root stopPropagation()s input to
    // shield the desktop beneath it, which would otherwise swallow these
    // before a bubble-phase window listener ever saw them.
    const opts = { passive: true, capture: true };
    window.addEventListener("mousemove", onMouseMove, opts);
    const events = ["mousedown", "keydown", "touchstart", "wheel"];
    for (const ev of events) {
      window.addEventListener(ev, onActivity, opts);
    }
    window.addEventListener("focus", onReturn);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("saat:force-idle", onForceIdle);
    arm();
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener("mousemove", onMouseMove, opts);
      for (const ev of events)
        window.removeEventListener(ev, onActivity, opts);
      window.removeEventListener("focus", onReturn);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("saat:force-idle", onForceIdle);
    };
  }, [timeoutMs]);

  return idle;
}
